import { config } from "../Environment";

export interface PrescriptionMetadata {
  _id: string;
  isFavorite?: boolean;
  folderId?: string;
  folderName?: string;
  folderColor?: string;
  tags?: string[];
  notes?: string;
  color?: string;
  spaceIds?: string[];
}

export interface Prescription {
  _id: string;
  name: string;
  fiUser: string;
  archived?: boolean;
  lastUpdated: string;
  createdOn: string;
  engagementCount?: number;
  metadata?: PrescriptionMetadata;
  // PWF Hybrid Bridge — present only on items sourced from the pwfs collection
  __isPwf?: boolean;
  manifestations?: Array<{ pwfType: string; isActive: boolean; enabledAt: string; config: Record<string, any> }>;
  pwfMeta?: { fiParent: string | null; ancestorIds: string[]; children: string[] };
  config?: Record<string, any>;
}

export interface GetPrescriptionsResponse {
  success?: boolean;
  count?: number;
  data?: Prescription[];
  // Alternative structure used by some endpoints
  prescriptions?: Prescription[];
}

// Shape of a prescription's parsed diagramJSON (version 2, current format).
// See frontend/src/components/reactflow/convert_prescription.js.
export interface PrescriptionDiagram {
  prescriptionLanguage?: string;
  // Which languages this activity supports — a plain list living alongside
  // prescriptionLanguage (the default/fallback), not a separate DB field.
  supportedLanguages?: string[];
  version?: number;
  nodes?: any[];
  edges?: any[];
  data?: Record<string, any>;
  flowcontext?: any[];
  contract?: any[];
  openExits?: any[];
  [key: string]: any;
}

export interface PrescriptionWithDiagram extends Prescription {
  diagram: PrescriptionDiagram;
  valid?: boolean;
}

/**
 * Fetches a single prescription by id, with its diagramJSON parsed into
 * `diagram` for convenience. Used by the Activity-content translation UI —
 * not a replacement for DesignView.tsx's own inline fetch/save, which
 * continues to work with the raw diagramJSON string unchanged.
 */
export const getPrescription = async (
  retryFetch: Function,
  id: string
): Promise<PrescriptionWithDiagram> => {
  const response = await retryFetch(`${config.API_BASE}/api/prescriptions/${id}`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch prescription: ${response.statusText}`);
  }

  const prescription = await response.json();
  const diagram: PrescriptionDiagram = prescription.diagramJSON
    ? JSON.parse(prescription.diagramJSON)
    : {};

  return { ...prescription, diagram };
};

/**
 * Saves a prescription's diagram. Mirrors the exact request shape
 * DesignView.tsx's own inline PUT already uses (`{ diagram, valid,
 * reminderSettings }`) — the backend replaces the whole diagramJSON from
 * `diagram`, so callers must pass back the full diagram object (e.g. the one
 * returned by `getPrescription`, mutated in place), not a partial patch.
 * `valid` is passed through unchanged from the fetched prescription so this
 * call doesn't silently clear it.
 */
export const updatePrescriptionDiagram = async (
  retryFetch: Function,
  id: string,
  diagram: PrescriptionDiagram,
  valid?: boolean
): Promise<void> => {
  const response = await retryFetch(`${config.API_BASE}/api/prescriptions/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ diagram, valid }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update prescription diagram: ${response.statusText}`);
  }
};

/**
 * Get all prescriptions for the authenticated user by email
 */
export const getUserPrescriptions = async (
  retryFetch: Function,
  userEmail: string,
  limit?: number
): Promise<GetPrescriptionsResponse> => {
  const url = limit
    ? `${config.API_BASE}/api/prescriptions/user/${encodeURIComponent(userEmail)}?limit=${limit}`
    : `${config.API_BASE}/api/prescriptions/user/${encodeURIComponent(userEmail)}`;

  const response = await retryFetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(
      error.error || `Failed to fetch prescriptions: ${response.statusText}`
    );
  }

  const result: GetPrescriptionsResponse = await response.json();
  return result;
};
